import React from 'react';
/**
 * @fileoverview Creates a context to allow teams to create an analytics tag hierarchy
 * for easily concatenating analytics tags based on the component hierarchy.
 *
 * Other components in the UI Library can use this context to generate analytics tags
 * automatically based on the context they find themselves in.
 *
 * Example:
 * <AnalyticsTagScopeProvider tag="my-component">
 *   <Link href="https://www.yext.com">Click me</Link>
 * </AnalyticsTagScopeProvider>
 *
 * Calling useAnalyticsTag in the Link will return "my-component-link"
 * (unless an analyticsTag was explicitly passed to the Link, which would take precedence)
 *
 * This works in series, so if you have a component that is wrapped in multiple scopes
 * it will produce a compound tag.
 *
 * Example:
 *
 * <AnalyticsTagScopeProvider tag="my-page">
 *   <AnalyticsTagScopeProvider tag="header">
 *     <Button>Click me</Button>
 *   </AnalyticsTagScopeProvider>
 * </AnalyticsTagScopeProvider>
 *
 * Calling useAnalyticsTag in the Button will return "my-page-header-button"
 * (unless an analyticsTag was explicitly passed to the Button, which would take precedence)
 *
 * As in Button and Link, setting the data-analytics attribute is the main use case for this.
 *
 * A call to useAnalyticsTag outside of an AnalyticsTagScopeProvider will return null.
 *
 * @author Quinn Ciccoretti (qciccoretti@yext.com)
 */

/**
 * Holds the storm analytics tag for the current scope.
 */
export const AnalyticsTagContext = React.createContext('');

/**
 * AnalyticsTagScopeProviderProps is the props for the AnalyticsTagScopeProvider component.
 */
export interface AnalyticsTagScopeProviderProps {
    tag: string;
}

/**
 * AnalyticsTagScopeProvider is a React context provider that will combine the passed in tag
 * with any parent tags to create a new storm analytics scope for children components to use when
 * generating their data-full-story attributes.
 */
export const AnalyticsTagScopeProvider = (props: React.PropsWithChildren<AnalyticsTagScopeProviderProps>) => {
  const { children, tag } = props;
  const combinedTag = concatWithParentAnalyticsTag(tag);
  return (
    <AnalyticsTagContext.Provider value={combinedTag}>
      {children}
    </AnalyticsTagContext.Provider>
  );
};
export default AnalyticsTagScopeProvider;

/**
 * concatWithParentAnalyticsTag will generate an analytics tag from the parent tag and the tag
 * passed in.
 */
function concatWithParentAnalyticsTag(tag:string) :string{
  const parentTag = useAnalyticsTagContext();
  return concatScopes(parentTag, tag);
}


/**
 * useAnalyticsTagContext will return the concatenated analytics tag from the full hierarchy of
 * analytics scope providers in the component hierarchy.
 */
export function useAnalyticsTagContext():string {
  return React.useContext(AnalyticsTagContext);
}

/**
 * useAnalyticsTag is meant to be used with the spread operator to set attributes, e.g.
 * const analyticsTag = useAnalyticsTag("button", props.explicitAnalyticsTag)
 * return <Button {...analyticsTag}></Button>
 *
 * useAnalyticsTag returns
 * - an object with the data-analytics field set to
 *    - the override if present
 *    - else, a computed tag if in a non-default analytics context (inside of at least one
 *    AnalyticsScopeProvider)
 * - an empty object if in the default analytics context (outside of AnalyticsTagScopeProvider),
 *
 */
export function useAnalyticsTag(defaultSuffix:string, override?:string) {
  const currentAnalyticsTagContext = useAnalyticsTagContext();
  // an explicit tag takes precedence over computed tags
  if(override){
    return {'data-analytics':override};
  }
  if(currentAnalyticsTagContext!=''){
    // only compute hierarchical tags if we have a non-default context
    return {'data-analytics':concatScopes(currentAnalyticsTagContext, defaultSuffix)}
  }
  return {};
}

/**
 * concatScopes will slugify and then concatenate two analytics scopes with a hyphen.
 */
function concatScopes(...scopes:string[]):string {
  return scopes
    .filter(scope => !!scope)
    .map(slugify)
    .join('-');
}

/**
 * slugify will convert a string to a slug by lower casing and then stripping special
 * characters and converting them to hyphens.
 */
function slugify(str:string):string{
  return (
    str
      .toLowerCase()
      .trim()
      // deburr accented characters
      .normalize('NFKD')
      // strip special characters
      .replace(/[^\w\s-]|[\s-]+/g, '-')
      .replace(/(-)\1+/g, '$1')
      .replace(/-$/, '')
  );
}
